import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface EscalationRow {
  readonly escalationId: string;
  readonly reviewId: string;
  readonly escalatedToName: string | null;
  readonly escalatedToCode: string | null;
  readonly escalationReasonCode: string;
  readonly escalationLevel: number | null;
  readonly status: string;
  readonly slaDeadlineAt: string | null;
  readonly decisionRemarks: string | null;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

export function ShEscalationPage(): ReactNode {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<EscalationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [toUser, setToUser] = useState('');
  const [reason, setReason] = useState('HIGH_VALUE_JOB');

  // Resolve form
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveRemarks, setResolveRemarks] = useState('');

  const load = useCallback(async () => {
    if (!reviewId) return;
    try {
      const r = await apiRequest<EscalationRow[]>(`/v1/sh/exceptions/escalation/review/${encodeURIComponent(reviewId)}`, { token });
      setRows(r); setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  const handleCreate = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest('/v1/sh/exceptions/escalation', {
        method: 'POST', token,
        body: { reviewId, escalatedToUserId: toUser, escalationReasonCode: reason },
      });
      setInfo(t('sh.escalation.created'));
      setShowCreate(false); setToUser('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, toUser, reason, token, busy, load]);

  const handleResolve = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!resolveId || busy) return;
    if (resolveRemarks.length < 10) { setError(t('sh.escalation.remarksMin')); return; }
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/sh/exceptions/escalation/${encodeURIComponent(resolveId)}/resolve`, {
        method: 'POST', token, body: { decisionRemarks: resolveRemarks },
      });
      setInfo(t('sh.escalation.resolvedMsg'));
      setResolveId(null); setResolveRemarks('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [resolveId, resolveRemarks, token, busy, load]);

  return (
    <main aria-label="sh-escalation">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to={`/sh/review/${reviewId}`} data-testid="back-review">{t('sh.common.review')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.escalation.title')}</h1>
        <Button type="button" variant="secondary" onClick={() => setShowCreate(!showCreate)} data-testid="btn-new-escalation"
                style={{ marginLeft: 'auto' }}>{t('sh.common.newBtn')}</Button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {showCreate && (
        <section style={{ padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }} aria-label="create-escalation-form">
          <form onSubmit={handleCreate} aria-label="escalation-form">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label htmlFor="esc-to" style={{ fontSize: 11 }}>{t('sh.escalation.escalateTo')}</label>
                <input id="esc-to" value={toUser} onChange={(e) => setToUser(e.target.value)} required data-testid="input-esc-to"
                       style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="esc-reason" style={{ fontSize: 11 }}>{t('sh.escalation.reason')}</label>
                <select id="esc-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="select-esc-reason"
                        style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                  <option value="HIGH_VALUE_JOB">{t('sh.escalation.reasonHighValue')}</option>
                  <option value="VARIANCE_EXCEEDED">{t('sh.escalation.reasonVariance')}</option>
                  <option value="BUDGET_ESCALATION">{t('sh.escalation.reasonBudget')}</option>
                  <option value="POLICY_EXCEPTION">{t('sh.escalation.reasonPolicy')}</option>
                  <option value="REPEATED_SLA_BREACH">{t('sh.escalation.reasonSla')}</option>
                </select>
              </div>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-escalation">{t('sh.common.create')}</Button>
            </div>
          </form>
        </section>
      )}

      {resolveId && (
        <section style={{ padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }} aria-label="resolve-escalation-form">
          <form onSubmit={handleResolve} aria-label="resolve-form">
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="esc-res" style={{ fontSize: 11 }}>{t('sh.escalation.decisionRemarks')}</label>
                <textarea id="esc-res" rows={2} value={resolveRemarks} onChange={(e) => setResolveRemarks(e.target.value)}
                          data-testid="input-esc-resolve"
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
              </div>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-resolve-esc">{t('sh.common.resolve')}</Button>
              <Button type="button" variant="danger" onClick={() => setResolveId(null)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.escalation.empty')}</p>
      ) : (
        <DataTable<EscalationRow>
          ariaLabel="escalation-table"
          rows={rows}
          rowKey={(r) => r.escalationId}
          columns={[
            { key: 'reason', header: t('sh.escalation.reason'), render: (r) => r.escalationReasonCode },
            { key: 'to', header: t('sh.escalation.colTo'), render: (r) => personLabel(r.escalatedToName, r.escalatedToCode) ?? '—' },
            { key: 'level', header: t('sh.common.level'), render: (r) => r.escalationLevel ?? '—' },
            { key: 'status', header: t('sh.common.status'), render: (r) => r.status },
            { key: 'slaDue', header: t('sh.common.slaDue'), render: (r) => (r.slaDeadlineAt ? at(r.slaDeadlineAt) : '—') },
            { key: 'created', header: t('sh.escalation.colCreated'), render: (r) => <span style={{ fontSize: 12 }}>{at(r.createdAt)}</span> },
            {
              key: 'actions', header: '',
              render: (r) => (
                r.status === 'PENDING' ? (
                  <Button type="button" variant="secondary" onClick={() => { setResolveId(r.escalationId); setResolveRemarks(''); }}
                          data-testid={`btn-resolve-${r.escalationId}`}>{t('sh.common.resolve')}</Button>
                ) : null
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
